import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/shared/lib/api-client";
import { API_ENDPOINTS } from "@/shared/constants/api-endpoints";
import { useHandlerCompanionEnabled } from "./companion-config";
import {
  HANDLER_MISSION_API,
  type HandlerMissionFlow,
} from "./handler-mission";
import { hqNextAction } from "./hq-next-action";
/**
 * WebSmSignOff — Agency HQ, the end of the day. Four moves: pass on a key
 * (recruit someone), take another mission, open their file (every debriefed
 * mission), and grant clearance — a link their people use to watch their
 * progress (the dossier: name, locks open, missions filed; never the debrief
 * text). Books a reminder only when the member selected a future time.
 * Simply visiting HQ never opts the member into a next-day text.
 *
 * What fires and when is authored on this step (ScheduleEvent /
 * ScheduleDelayMinutes / ScheduleNotBeforeHour) and read server-side.
 *
 * Options: Title / Message / ScheduleLabel
 *   / BgColor / AccentColor. Terminal — emits nothing.
 */
import { useState } from "react";
import { isAxiosError } from "axios";
import { toast } from "sonner";
import { PRIMARY_BTN_CLASS, SECTION_CLASS, stepVars } from "./step-shell";
import type { StepTypeProps } from "./types";
import { LegendCarousel } from "./legend-carousel";
import { HqConsole } from "./hq-console";
import { DoorView } from "./vault-run";
import { WINDOW_MS } from "./lid-clock";
import { tierOfLevel } from "./open-case";
import { comebackLoopEnabled } from "./comeback-config";
import { LOCK_WORDS } from "./vault-clips";
import { feltDelta, streakFrom } from "../streak";
import { fetchClearanceLink } from "@/features/funnel/api/use-mission-file";
import { useStepperStore } from "@/store/stepper";
import { useFunnelStore } from "@/store";
import { LoginModal } from "@/features/funnel/components/login-modal";
import {
  useAcceptedMission,
  useMissionFile,
  useNextDayBooking,
} from "@/features/funnel/api/use-mission-file";

export default function WebSmSignOffType({ getOpt }: StepTypeProps) {
  const companionEnabled = useHandlerCompanionEnabled();
  const bg = getOpt("BgColor") || "#0a0809";
  const accent = getOpt("AccentColor") || "#d6a94e";
  const title = getOpt("Title") || "Agency HQ";
  const message = getOpt("Message") || "What's your next move, operative?";
  const nextMissionAt = useFunnelStore((s) => s.nextMissionAt);
  // the Comeback Loop: tomorrow's case, sealed on the way out, waits here
  const sealed = useFunnelStore((s) => s.sealed);
  const scheduleLabel = nextMissionAt
    ? "Text me my next mission"
    : getOpt("ScheduleLabel") || "Text me tomorrow for my next mission";

  const adventureId = useStepperStore((s) => s.adventureId);
  const stepName = useStepperStore((s) => s.currentStepName);
  // unread (loading, or failed) is not "none filed": no continue-mission
  // panel for a mission that may be filed, no first-mission doors
  const {
    data: missions = [],
    isSuccess: fileLoaded,
    isError: fileFailed,
    refetch: refetchFile,
  } = useMissionFile();
  // unread, no count: HQ shows a dash, not "0 filed"
  const missionCount = fileLoaded ? missions.length : null;
  const retryFile = fileFailed ? () => void refetchFile() : undefined;
  const comeback = comebackLoopEnabled()
    ? {
        nextLock:
          missionCount !== null && missionCount < LOCK_WORDS.length
            ? LOCK_WORDS[missionCount]
            : null,
        opensAt: sealed?.at ?? null,
        lastFelt: missions[0] ? feltDelta(missions[0].howItFelt) : null,
        streak: streakFrom(
          missions
            .map((m) => (m.filedAt ? new Date(m.filedAt).getTime() : NaN))
            .filter((t) => !Number.isNaN(t)),
        ),
        clearance: fetchClearanceLink,
      }
    : null;
  // a picked time already gone (its text has been sent) is not booked again
  // for "now": returning to HQ does not opt into another reminder
  const [bookAt] = useState(() =>
    nextMissionAt && nextMissionAt > Date.now() ? nextMissionAt : null,
  );
  const booking = useNextDayBooking(adventureId, stepName, bookAt);
  const [loginOpen, setLoginOpen] = useState(false);
  const [fileOpen, setFileOpen] = useState(false);
  // their door, at the end of the hallway, as it stands
  const [doorOpen, setDoorOpen] = useState(false);
  const goTo = useStepperStore((s) => s.goTo);
  const hasFirstDoors = useStepperStore((s) => !!s.graph?.steps["First doors"]);
  // A mission accepted and not in their file yet: HQ continues it rather
  // than offering another.
  const acceptedAt = useFunnelStore((s) => s.acceptedAt);
  const storedTier = useFunnelStore((s) => s.tier);
  const { data: accepted } = useAcceptedMission();
  const tier = storedTier ?? tierOfLevel(accepted?.level) ?? "beginner";
  const ongoing =
    fileLoaded &&
    accepted &&
    acceptedAt &&
    !missions.some(
      (m) => m.name.trim().toUpperCase() === accepted.name.trim().toUpperCase(),
    )
      ? { name: accepted.name, endsAt: acceptedAt + WINDOW_MS, tier }
      : null;

  const eligible = companionEnabled && fileLoaded && missions.length >= 3;
  const handlerState = useQuery({
    queryKey: ["secretmissions", "hq-handler", adventureId, missionCount],
    enabled: eligible,
    staleTime: 0,
    queryFn: async () => {
      const [selection, saved] = await Promise.all([
        apiClient.get<{ result: { handler: string | null } }>(
          API_ENDPOINTS.companion.open,
        ),
        apiClient.get<{ result: { flow: HandlerMissionFlow | null } }>(
          HANDLER_MISSION_API,
        ),
      ]);
      return {
        handler: selection.data.result.handler,
        flow: saved.data.result.flow,
      };
    },
  });
  const next = hqNextAction({
    completed: eligible && !handlerState.isSuccess ? null : missionCount,
    handler: handlerState.data?.handler,
    phase: handlerState.data?.flow?.phase,
    ongoing: !!ongoing,
    firstDoors: hasFirstDoors,
  });
  const nextMission = () => {
    if (eligible && handlerState.isError) {
      void handlerState.refetch();
      return;
    }
    if (!next.step) return;
    const saved = handlerState.data?.flow;
    if (saved?.phase === "accepted")
      useFunnelStore.setState({
        missionId: 0,
        handler: handlerState.data?.handler ?? null,
        tier: "beginner",
        acceptedAt: Date.parse(saved.acceptedAt || "") || Date.now(),
      });
    goTo(next.step);
  };

  // Retry. With no phone on the member yet, get one, then book (onSignedIn).
  const book = async () => {
    const { error } = await booking.refetch();
    if (!error) return;
    if (
      isAxiosError(error) &&
      error.response?.data?.message === "Phone required"
    ) {
      setLoginOpen(true);
      return;
    }
    toast.error("Couldn't set up your next mission's text — try again.");
  };

  return (
    <section
      style={stepVars({ accent, bg })}
      className={`${SECTION_CLASS} flex-col p-0`}
    >
      <HqConsole
        title={title}
        message={message}
        missionCount={missionCount}
        onRetryFile={retryFile}
        // nothing filed yet: their first mission's doors (the Standard case
        // ready, no case desk), where the adventure has them
        onNextMission={
          companionEnabled
            ? nextMission
            : () =>
                goTo(
                  fileLoaded && missions.length === 0 && hasFirstDoors
                    ? "First doors"
                    : "Doors",
                )
        }
        nextMissionLabel={
          companionEnabled
            ? eligible && handlerState.isError
              ? "Retry your progress"
              : next.label
            : undefined
        }
        nextMissionHint={
          companionEnabled && next.step === "Handlers"
            ? "Your handler will help shape what comes next."
            : undefined
        }
        ongoing={ongoing}
        onContinue={companionEnabled ? nextMission : () => goTo("Delivered")}
        sealed={comebackLoopEnabled() ? sealed : null}
        comeback={comeback}
        onLegend={() => setFileOpen(true)}
        // ponytail: the circle (sharing the dossier link via
        // fetchClearanceLink) is held back for now
        onDoor={() => setDoorOpen(true)}
      />
      {doorOpen && missionCount !== null ? (
        <DoorView
          locksOpen={Math.min(missionCount, 7)}
          onClose={() => setDoorOpen(false)}
        />
      ) : null}
      {/* booked quietly; only a failure needs them */}
      {booking.isError ? (
        <button
          type="button"
          onClick={book}
          className={`mt-4 ${PRIMARY_BTN_CLASS}`}
        >
          {scheduleLabel}
        </button>
      ) : null}

      {fileOpen ? (
        <LegendCarousel
          missions={fileLoaded ? missions : null}
          onRetry={retryFile}
          onClose={() => setFileOpen(false)}
        />
      ) : null}

      <LoginModal
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        onSignedIn={() => void book()}
        accent={accent}
      />
    </section>
  );
}
